/**
 * HealthBar — a value rendered as a single continuous fill meter.
 *
 *   level 8/10, success →  ▰▰▰▰▰▰▰▰░░  (one smooth bar, ~80% filled)
 *   level 3/10, danger  →  ▰▰▰░░░░░░░
 *   level 0,    neutral →  ░░░░░░░░░░
 *
 * The health meter shared by the home Company-health board (department rows +
 * the overall score). A muted full-round track (`bg-foreground/10`) holds a
 * single rounded fill in the intent hue, sized to `value/max`. This reads as
 * one calm progress bar rather than a row of blinking segments — the segmented
 * "8-bit" rendering it replaced turned a board of rows into visual confetti.
 * The fill always spans its container, so there's no fixed width to keep in
 * sync with the layout; pass `className` (e.g. `w-24`) for a fixed footprint.
 */

import { cn } from "../utils";
import type { Intent } from "../tokens/intents";

const FILL_BG: Record<Intent, string> = {
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  neutral: "bg-foreground/55",
};

export function HealthBar({
  value,
  max = 10,
  intent = "neutral",
  tall = false,
  className,
}: {
  /** Current fill, 0..max. */
  value: number;
  /** Full-scale value (default 10). */
  max?: number;
  /**
   * Retained for API compatibility; the meter is now continuous, so the
   * segment count no longer affects rendering.
   * @deprecated the bar is no longer segmented
   */
  segments?: number;
  /** Working color of the fill. */
  intent?: Intent;
  /** Taller bar for the hero / overall meter. */
  tall?: boolean;
  className?: string;
}) {
  const ratio = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  const pct = Math.round(ratio * 100);
  // Any positive value keeps a visible sliver, so "barely on track" never
  // reads as empty (empty is reserved for genuine no-data).
  const width = value <= 0 ? 0 : Math.max(4, pct);

  return (
    <span
      role="img"
      aria-label={`Health ${pct} percent`}
      className={cn(
        "block w-full overflow-hidden rounded-full bg-foreground/10",
        tall ? "h-2" : "h-1.5",
        className,
      )}
    >
      <span
        aria-hidden
        className={cn("block h-full rounded-full", FILL_BG[intent])}
        style={{ width: `${width}%` }}
      />
    </span>
  );
}
